// 📄 userService.ts
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteField,
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  limit,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Unsubscribe
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functionsClient } from '../firebase';
import { ensurePersonalOrganization } from './organizationService.ts';
import { PLAN_DEFAULT_ID, PlanFeatures } from './planService.ts';
import { OrgMember, UserOrgLink, PendingInvitation } from '../types/organization';

export type UserRole = 'admin' | 'consultant';

// Límites de arranque para cuentas nuevas (freno anti-abuso, ver
// proyecto_limites_organizacion, rediseño 2026-08-19) — editables por un
// Admin de plataforma después, POR USUARIO (no por organización): un
// mismo usuario puede dueñar varias orgs, así que un tope por-org se
// podía esquivar creando más orgs. `limiteProyectos` es un tope TOTAL
// agregado (suma de proyectos creados por este usuario en TODAS sus
// orgs, incluso orgs ajenas donde solo es Admin); `limiteMiembrosPorProyecto`
// y `limiteMiembrosPorOrganizacion` se aplican por-contenedor (cada
// proyecto/org que este usuario cree puede llegar hasta ese tope, sin
// sumarse entre sí).
// Deben coincidir con el plan 'free' de planService.ts (PLAN_DEFAULT_ID) —
// viven duplicados acá en vez de leerse de ahí porque ensureUserProfile no
// puede esperar una escritura ya resuelta al catálogo; si se ajustan los
// límites del plan Free, actualizar también estas 5 constantes (y el guard
// de autoprovisión en firestore.rules → match /users/{userId} → allow
// create, que hardcodea estos mismos valores para no "confiar en el
// cliente").
export const LIMITE_EQUIPOS_DEFAULT = 1;
export const LIMITE_PROYECTOS_DEFAULT = 2;
export const LIMITE_MIEMBROS_POR_PROYECTO_DEFAULT = 5;
export const LIMITE_MIEMBROS_POR_ORGANIZACION_DEFAULT = 1;
export const LIMITE_CREDENCIALES_VAULT_DEFAULT = 3;
export const LIMITE_API_TESTER_MENSUAL_DEFAULT = 50;
export const LIMITE_ONEBOT_MENSUAL_DEFAULT = 30;

export interface UserProfile {
  uid: string;
  email: string;
  role: UserRole;
  createdAt?: any;
  // Solo lo traen las cuentas creadas por otro miembro (ver
  // createProjectMember), que sí piden nombre completo al invitar.
  // Editable por el propio usuario desde "Mi perfil".
  name?: string;
  // País de residencia, editable desde "Mi perfil" — usado para mostrar
  // zona horaria en reuniones y coordinación de equipo.
  pais?: string;
  // true para cuentas creadas por otro miembro (ver createProjectMember):
  // fuerza la pantalla de cambio de contraseña antes de dejar continuar.
  requiereCambioPassword?: boolean;
  // Freno anti-abuso (proyecto_limites_organizacion): cuántos equipos
  // (organizations tipo 'equipo') puede crear esta cuenta como máximo, y
  // cuántos lleva creados hoy. `cantidadEquiposCreados` lo mantiene SOLO
  // el trigger onOrgCreated/onOrgDeleted (Admin SDK) — nunca el cliente,
  // ver el guard dedicado en firestore.rules → match /users/{userId}.
  limiteEquipos?: number;
  cantidadEquiposCreados?: number;
  // Tope TOTAL agregado de proyectos que este usuario puede crear, sumando
  // TODAS sus organizaciones (propias o ajenas donde solo es Admin) —
  // `cantidadProyectosCreados` lo mantiene SOLO el trigger onProjectCreated
  // (Admin SDK). Rediseño 2026-08-19, ver [[proyecto_limites_organizacion]].
  limiteProyectos?: number;
  cantidadProyectosCreados?: number;
  // Tope de miembros DENTRO de cada proyecto que este usuario cree (no
  // agregado — cada proyecto puede llegar hasta este número, sin sumarse
  // entre proyectos). Sin contador propio: se compara contra
  // projects/{id}.miembrosUids.size() directo en firestore.rules.
  limiteMiembrosPorProyecto?: number;
  // Tope de miembros DENTRO de cada organización que este usuario posea
  // (ownerId) — mismo criterio "por-contenedor, no agregado" que arriba.
  // El contador SÍ sigue siendo por-org (organizations/{orgId}.cantidadMiembros,
  // mantenido por orgMemberLimits.js), solo el LÍMITE se centralizó acá.
  limiteMiembrosPorOrganizacion?: number;
  // Tope TOTAL agregado de credenciales que este usuario puede crear en el
  // Vault de proyectos (projects/{id}/credenciales), sumando TODOS los
  // proyectos donde las cree — mismo criterio "agregado" que
  // `limiteProyectos`. A diferencia de los otros 4 campos, firestore.rules
  // NO enforcea esto: el Vault se crea vía credentialManager.js (Cloud
  // Function con Admin SDK, que bypassea las rules), así que el chequeo y
  // el mantenimiento de `cantidadCredencialesCreadas` viven ahí mismo, no
  // en un trigger de Firestore.
  limiteCredencialesVault?: number;
  cantidadCredencialesCreadas?: number;
  // Cuota MENSUAL (no de por vida como los de arriba) de "API Tester" —
  // compartida entre ariba_ExecuteRequest, Buscar Proveedores y Buscar
  // Facturas (ver functions/services/monthlyQuota.js). `usoApiTesterMes`
  // guarda a qué mes ("YYYY-MM") corresponde `usoApiTesterCantidad`; si no
  // coincide con el mes actual, el mismo callable lo trata como 0 (reset
  // perezoso, sin cron). Igual que credenciales del Vault, firestore.rules
  // NO enforcea el chequeo (vive en el callable, Admin SDK) — solo evita
  // que el propio usuario se autoescriba estos campos.
  limiteApiTesterMensual?: number;
  usoApiTesterMes?: string;
  usoApiTesterCantidad?: number;
  // Cuota MENSUAL de OneBot (Nexus AI) — mismo criterio que API Tester,
  // pero consumida desde functions/triggers/onebotGemini.js (un trigger de
  // Firestore, no un callable): un mensaje bloqueado por cuota se responde
  // DENTRO del chat en vez de un error, y no incrementa el contador.
  limiteOneBotMensual?: number;
  usoOneBotMes?: string;
  usoOneBotCantidad?: number;
  // "Planes/Suscripciones" (Fase 5/5, 2026-08-26, ver planService.ts) —
  // solo modelo de planes, sin cobro real. `planId` es el plan asignado
  // por un Admin (arranca en 'free' para cuentas nuevas); `features` es
  // el gate de las 2 funciones que consumen APIs externas de costo real
  // (Slack webhooks, Búsqueda IA). Ambos se ESCRIBEN denormalizados acá
  // (no se resuelven contra el catálogo de planes en tiempo real) para
  // que tanto el cliente como las Cloud Functions puedan leer un simple
  // booleano sin compartir código entre src/ y functions/.
  planId?: string;
  features?: PlanFeatures;
}

// Cuenta(s) sembradas como admin la primera vez que se crea o se completa su perfil.
// No pisa el rol de un usuario que ya tiene uno asignado.
const BOOTSTRAP_ADMIN_EMAILS = ['yeison.ortiz0321@gmail.com'];

/**
 * Garantiza que exista users/{uid} con un rol asignado, sin romper
 * documentos que ya vienen guardando datos de commerceIntegration.
 * No sobreescribe un `role` ya existente.
 *
 * También garantiza que el usuario tenga su organización personal (ver
 * ensurePersonalOrganization) — aplica a los 3 flujos de login (registro
 * tradicional, Google, Microsoft), porque todos pasan por acá desde
 * AuthContext. Es un paso aditivo e idempotente: si la creación de la org
 * personal falla, no se interrumpe el login (se loguea el error y sigue),
 * porque el perfil de usuario ya quedó garantizado arriba — la Sub-fase C
 * (backfill) es la red de seguridad para cualquier usuario que se quede
 * sin org personal por una falla puntual acá.
 */
export const ensureUserProfile = async (uid: string, email: string | null, displayName?: string | null) => {
  if (!uid) return;
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  const seededRole: UserRole = email && BOOTSTRAP_ADMIN_EMAILS.includes(email.toLowerCase())
    ? 'admin'
    : 'consultant';

  if (!snap.exists()) {
    await setDoc(ref, {
      email: email ? email.toLowerCase() : '',
      // Cuentas creadas por invitación (proyecto/organización) traen el nombre en Auth.
      ...(displayName ? { name: displayName } : {}),
      role: seededRole,
      createdAt: serverTimestamp(),
      limiteEquipos: LIMITE_EQUIPOS_DEFAULT,
      limiteProyectos: LIMITE_PROYECTOS_DEFAULT,
      limiteMiembrosPorProyecto: LIMITE_MIEMBROS_POR_PROYECTO_DEFAULT,
      limiteMiembrosPorOrganizacion: LIMITE_MIEMBROS_POR_ORGANIZACION_DEFAULT,
      limiteCredencialesVault: LIMITE_CREDENCIALES_VAULT_DEFAULT,
      limiteApiTesterMensual: LIMITE_API_TESTER_MENSUAL_DEFAULT,
      limiteOneBotMensual: LIMITE_ONEBOT_MENSUAL_DEFAULT,
      planId: PLAN_DEFAULT_ID,
      features: { slack: false, busquedaIA: false } as PlanFeatures
      // Los contadores (cantidadEquiposCreados/cantidadProyectosCreados/
      // cantidadCredencialesCreadas) NO se setean acá — arrancan ausentes
      // (la regla los trata como 0) y los escriben los triggers/callables
      // correspondientes recién cuando este usuario crea su primer
      // equipo/proyecto/credencial.
    }, { merge: true });
  } else if (!snap.data()?.role) {
    await setDoc(ref, { role: seededRole, email: email || snap.data()?.email || '' }, { merge: true });
  }

  try {
    await ensurePersonalOrganization(uid, email || '', displayName);
  } catch (err) {
    console.error('Error creando organización personal:', err);
  }

  // Sub-fase M1+M2: aplica cualquier invitación pendiente a organizaciones
  // que coincida con el email de ESTA cuenta — mismo criterio aditivo y
  // aislado que ensurePersonalOrganization arriba, para no interrumpir el
  // login si falla.
  try {
    await processOrgInvitations(uid, email);
  } catch (err) {
    console.error('Error procesando invitaciones de organización pendientes:', err);
  }
};

/**
 * Busca TODAS las invitaciones pendientes (en cualquier organización) que
 * coincidan con este email — collectionGroup query, porque el cliente no
 * conoce de antemano en qué orgIds podría haber una invitación. Por cada
 * una encontrada: crea el doc de membresía + su mirror, y borra la
 * invitación consumida. Idempotente y seguro de reintentar: si ya no
 * queda ninguna invitación con ese email (ya procesada, o nunca existió),
 * no hace ningún write.
 */
export const processOrgInvitations = async (uid: string, email: string | null): Promise<void> => {
  if (!uid || !email) return;
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) return;

  const q = query(collectionGroup(db, "invitacionesPendientes"), where("email", "==", normalizedEmail));
  const snap = await getDocs(q);
  if (snap.empty) return;

  const batch = writeBatch(db);
  snap.docs.forEach((invDoc) => {
    const data = invDoc.data() as PendingInvitation;
    batch.set(doc(db, "organizations", data.orgId, "miembros", uid), {
      uid,
      email: normalizedEmail,
      role: data.rol,
      joinedAt: serverTimestamp()
    } as OrgMember);
    batch.set(doc(db, "usuarios", uid, "organizaciones", data.orgId), {
      orgId: data.orgId,
      role: data.rol,
      type: data.orgType,
      joinedAt: serverTimestamp()
    } as UserOrgLink);
    batch.delete(invDoc.ref);
  });
  await batch.commit();
};

export const subscribeUserProfile = (
  uid: string,
  callback: (profile: UserProfile | null) => void
): Unsubscribe => {
  const ref = doc(db, "users", uid);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) {
      callback(null);
      return;
    }
    const data = snap.data();
    callback({
      uid,
      email: data.email || '',
      role: (data.role as UserRole) || 'consultant',
      createdAt: data.createdAt,
      name: data.name || undefined,
      pais: data.pais || undefined,
      requiereCambioPassword: !!data.requiereCambioPassword,
      // Freno anti-abuso (proyecto_limites_organizacion) — se suman acá
      // (no solo en listPlatformUsers) para que cualquier pantalla que ya
      // consume useAuth().profile pueda mostrar "X de Y" sin un fetch
      // aparte, y en TIEMPO REAL: este onSnapshot ya escucha users/{uid},
      // así que un contador actualizado por un trigger (ej. al crear un
      // proyecto) se refleja solo, sin recargar la página.
      limiteEquipos: data.limiteEquipos,
      cantidadEquiposCreados: data.cantidadEquiposCreados,
      limiteProyectos: data.limiteProyectos,
      cantidadProyectosCreados: data.cantidadProyectosCreados,
      limiteMiembrosPorProyecto: data.limiteMiembrosPorProyecto,
      limiteMiembrosPorOrganizacion: data.limiteMiembrosPorOrganizacion,
      limiteCredencialesVault: data.limiteCredencialesVault,
      cantidadCredencialesCreadas: data.cantidadCredencialesCreadas,
      limiteApiTesterMensual: data.limiteApiTesterMensual,
      usoApiTesterMes: data.usoApiTesterMes,
      usoApiTesterCantidad: data.usoApiTesterCantidad,
      limiteOneBotMensual: data.limiteOneBotMensual,
      usoOneBotMes: data.usoOneBotMes,
      usoOneBotCantidad: data.usoOneBotCantidad,
      planId: data.planId,
      features: data.features
    });
  });
};

/**
 * El dueño del perfil edita su propio nombre y/o país desde "Mi perfil".
 * Nunca toca `role` — firestore.rules ya bloquea ese campo en un update
 * hecho por el propio usuario (ver match /users/{userId}).
 */
export const updateOwnProfile = async (uid: string, data: { name?: string; pais?: string }) => {
  if (!uid) throw new Error("UID no proporcionado.");
  const payload: Record<string, string> = {};
  if (data.name !== undefined) payload.name = data.name.trim();
  if (data.pais !== undefined) payload.pais = data.pais;
  if (Object.keys(payload).length === 0) return;

  const ref = doc(db, "users", uid);
  await updateDoc(ref, payload);
};

/**
 * Se llama tras un cambio de contraseña exitoso en la pantalla obligatoria
 * (ver CambiarPasswordObligatorio). No toca `role`, así que no necesita
 * ningún permiso extra sobre el que ya tiene el dueño del perfil.
 */
export const clearRequiereCambioPassword = async (uid: string) => {
  const ref = doc(db, "users", uid);
  await updateDoc(ref, { requiereCambioPassword: false });
};

export const setUserRole = async (uid: string, role: UserRole) => {
  if (!uid) throw new Error("UID no proporcionado.");
  const ref = doc(db, "users", uid);
  await updateDoc(ref, { role });
};

/**
 * Lista solo los documentos de `users` que representan cuentas de acceso
 * a la plataforma (tienen `role`), excluyendo los registros de clientes
 * creados desde "Registrar Cliente" que comparten la misma colección
 * pero no tienen ese campo.
 */
export const listPlatformUsers = async (): Promise<UserProfile[]> => {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs
    .filter(d => !!d.data()?.role)
    .map(d => ({
      uid: d.id,
      email: d.data().email || '',
      role: (d.data().role as UserRole) || 'consultant',
      createdAt: d.data().createdAt,
      name: d.data().name || undefined,
      limiteEquipos: d.data().limiteEquipos,
      cantidadEquiposCreados: d.data().cantidadEquiposCreados,
      limiteProyectos: d.data().limiteProyectos,
      cantidadProyectosCreados: d.data().cantidadProyectosCreados,
      limiteMiembrosPorProyecto: d.data().limiteMiembrosPorProyecto,
      limiteMiembrosPorOrganizacion: d.data().limiteMiembrosPorOrganizacion,
      limiteCredencialesVault: d.data().limiteCredencialesVault,
      cantidadCredencialesCreadas: d.data().cantidadCredencialesCreadas,
      limiteApiTesterMensual: d.data().limiteApiTesterMensual,
      usoApiTesterMes: d.data().usoApiTesterMes,
      usoApiTesterCantidad: d.data().usoApiTesterCantidad,
      limiteOneBotMensual: d.data().limiteOneBotMensual,
      usoOneBotMes: d.data().usoOneBotMes,
      usoOneBotCantidad: d.data().usoOneBotCantidad,
      planId: d.data().planId,
      features: d.data().features
    }));
};

/**
 * Setter genérico para un campo de límite en users/{uid} — solo un Admin
 * puede escribir estos campos (ver el guard dedicado en firestore.rules →
 * match /users/{userId}), así que esto falla con permission-denied para
 * cualquier otro usuario. `limite: null` borra el campo (usuario queda
 * sin límite). Ver proyecto_limites_organizacion.
 */
const updateUserLimite = async (uid: string, campo: string, limite: number | null): Promise<void> => {
  const ref = doc(db, "users", uid);
  if (limite === null) {
    await updateDoc(ref, { [campo]: deleteField() });
    return;
  }
  if (!Number.isFinite(limite) || limite < 0) throw new Error("El límite debe ser un número mayor o igual a 0.");
  await updateDoc(ref, { [campo]: limite });
};

export const updateUserLimiteEquipos = (uid: string, limite: number | null) =>
  updateUserLimite(uid, 'limiteEquipos', limite);
export const updateUserLimiteProyectos = (uid: string, limite: number | null) =>
  updateUserLimite(uid, 'limiteProyectos', limite);
export const updateUserLimiteMiembrosPorProyecto = (uid: string, limite: number | null) =>
  updateUserLimite(uid, 'limiteMiembrosPorProyecto', limite);
export const updateUserLimiteMiembrosPorOrganizacion = (uid: string, limite: number | null) =>
  updateUserLimite(uid, 'limiteMiembrosPorOrganizacion', limite);
export const updateUserLimiteCredencialesVault = (uid: string, limite: number | null) =>
  updateUserLimite(uid, 'limiteCredencialesVault', limite);
export const updateUserLimiteApiTesterMensual = (uid: string, limite: number | null) =>
  updateUserLimite(uid, 'limiteApiTesterMensual', limite);
export const updateUserLimiteOneBotMensual = (uid: string, limite: number | null) =>
  updateUserLimite(uid, 'limiteOneBotMensual', limite);

/**
 * Lista SOLO las cuentas con role:"admin" (acceso total a la plataforma)
 * — a diferencia de listPlatformUsers, es una query filtrada por `role`,
 * no un listado completo + filtro en el cliente. A propósito: a medida
 * que crezca la cantidad de consultores en la plataforma, el panel de
 * "Gestión de Usuarios" no debe traer cientos de cuentas para mostrar un
 * puñado de admins — separado explícitamente de "Gestionar miembros" (a
 * nivel de organización, ver organizationService.ts), que es donde se
 * gestiona el día a día de quién entra a un equipo.
 */
export const listPlatformAdmins = async (): Promise<UserProfile[]> => {
  const q = query(collection(db, "users"), where("role", "==", "admin"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    uid: d.id,
    email: d.data().email || '',
    role: 'admin' as UserRole,
    createdAt: d.data().createdAt,
    name: d.data().name || undefined
  }));
};

/**
 * Crea una cuenta nueva DIRECTAMENTE como Admin de plataforma (Firebase
 * Auth + perfil), vía Cloud Function con Admin SDK — mismo motivo que
 * createProjectMemberAccount (no reemplazar la sesión de quien la crea).
 * El backend (createPlatformAdmin.js) exige que quien llama YA sea admin;
 * acá no se repite ese chequeo porque las rules/Cloud Function son la
 * autoridad real, esto es solo el wrapper del cliente.
 */
export const createPlatformAdminAccount = async (data: {
  email: string;
  nombreCompleto: string;
  passwordTemporal: string;
}): Promise<{ uid: string }> => {
  const callable = httpsCallable(functionsClient, 'createPlatformAdmin');
  const result = await callable(data);
  return result.data as { uid: string };
};

/**
 * Busca una cuenta de plataforma por email exacto, para el flujo de
 * "invitar miembro a un proyecto". Por ahora exige que el usuario ya
 * exista en el sistema (sin invitaciones pendientes). Es una query
 * filtrada (no un listado completo) para calzar con la regla de
 * Firestore que permite `list` a cualquier usuario autenticado sobre
 * `users`, ya que solo expone email/role, nunca datos sensibles.
 */
export const findPlatformUserByEmail = async (email: string): Promise<UserProfile | null> => {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return null;

  const q = query(collection(db, "users"), where("email", "==", normalized), limit(1));
  const snap = await getDocs(q);
  const match = snap.docs.find(d => !!d.data()?.role);
  if (!match) return null;

  return {
    uid: match.id,
    email: match.data().email || '',
    role: (match.data().role as UserRole) || 'consultant',
    createdAt: match.data().createdAt
  };
};
